import { useState, useEffect, useCallback, useRef } from 'react';
import { FaClipboard, FaCheck } from 'react-icons/fa';
import useLimits from '../../hooks/useLimits';
import { useAuth } from '../../context/AuthContext';
import { auth, db } from '../../firebase'; // NEW
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore'; // NEW

let inFlightMap = {};
const isAudioRequestInFlight = (contentId) => !!inFlightMap[contentId];
const markAudioRequest = (contentId, state) => {
  if (!contentId) return;
  if (state) inFlightMap[contentId] = true;
  else delete inFlightMap[contentId];
};

const CF_AUDIO_EXPLAINER_URL = "https://asia-south1-xskills-e47b7.cloudfunctions.net/create_audio_explainer";

const AudioExplainer = ({ title, contentId }) => {
  const [audioData, setAudioData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef(null);

  const { audio: audioLimit } = useLimits();
  const { user } = useAuth() || {};

  // Helper: fetch latest Audio doc if it exists
  const fetchLatestAudio = useCallback(async () => {
    try {
      const uid = user?.uid || auth?.currentUser?.uid;
      if (!uid || !contentId) return null;
      const col = collection(db, 'users', uid, 'Sessions', contentId, 'Audio');
      const q = query(col, orderBy('timestamp', 'desc'), limit(1));
      const snap = await getDocs(q);
      if (snap && !snap.empty) {
        const doc = snap.docs[0].data() || {};
        return {
          title: doc.title || title || "Audio Explainer",
          transcript: doc.transcript || "",
          audio_url: doc.audio_url || ""
        };
      }
    } catch (e) {
      // ignore, fallback to generation
    }
    return null;
  }, [user?.uid, contentId, title]);

  // Get ID token robustly
  const getAuthToken = useCallback(async () => {
    try {
      if (user?.getIdToken) return await user.getIdToken();
    } catch {}
    try {
      if (auth?.currentUser?.getIdToken) return await auth.currentUser.getIdToken();
    } catch {}
    try {
      if (window?.firebaseAuth?.currentUser?.getIdToken) {
        return await window.firebaseAuth.currentUser.getIdToken();
      }
    } catch {}
    return null;
  }, [user]);

  const copyTranscript = () => {
    if (!audioData?.transcript) return;
    navigator.clipboard.writeText(audioData.transcript);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const generateAudioExplainer = useCallback(
    async (force = false) => {
      if (!contentId) return;
      if (!force && audioData) return;
      if (audioLimit !== undefined && audioLimit <= 0 && !force) {
        setError("You have reached your daily audio explainer limit.");
        setIsGenerating(false);
        return;
      }
      if (isAudioRequestInFlight(contentId)) return;

      setIsGenerating(true);
      setError(null);

      // First try to use existing audio if not forcing
      if (!force) {
        const existing = await fetchLatestAudio();
        if (existing) {
          setAudioData(existing);
          setIsGenerating(false);
          return;
        }
      }

      markAudioRequest(contentId, true);
      try {
        const token = await getAuthToken();
        if (!token) {
          setError("Authentication required. Please sign in and try again.");
          setIsGenerating(false);
          return;
        }

        const resp = await fetch(CF_AUDIO_EXPLAINER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ contentId, title, force: !!force })
        });

        if (resp.status === 401 || resp.status === 403) {
          setError("Session expired. Please sign in again, then press Try Again.");
          setAudioData(null);
          return;
        }

        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json.success) {
          throw new Error(json?.error || `Request failed (${resp.status})`);
        }

        setAudioData({
          transcript: json.transcript,
          audio_url: json.audio_url,
          title: json.title || title || "Audio Explainer"
        });
      } catch (e) {
        setError((e && e.message) ? e.message : "Failed to generate audio explainer. Please try again.");
        setAudioData(null);
      } finally {
        markAudioRequest(contentId, false);
        setIsGenerating(false);
      }
    },
    [contentId, title, audioData, audioLimit, getAuthToken, fetchLatestAudio]
  );

  useEffect(() => {
    (async () => {
      if (!contentId) return;
      // Do not force on mount; use existing audio if present
      await generateAudioExplainer(false);
    })();
  }, [contentId, generateAudioExplainer]);

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col bg-white mb-10"
      style={{ height: "730px", overflowY: "auto", padding: "24px" }}
    >
      <div className="flex-grow">
        <div className="border-b border-gray-200 flex flex-wrap sm:flex-nowrap justify-between items-center gap-2">
          <div className="flex flex-wrap mt-1 text-xs text-gray-500 gap-1">
            <span className="bg-purple-100 text-purple-800 px-4 py-0.5 rounded-full">
              AI Audio
            </span>
            {audioData?.title && (
              <span className="font-semibold text-gray-600 truncate max-w-xs">
                {audioData.title}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => generateAudioExplainer(true)} // force only on user action
              disabled={isGenerating}
              className={`p-2 ${isGenerating ? "text-gray-400" : "text-gray-600 hover:text-purple-700 hover:bg-purple-50"} rounded-md transition-colors flex items-center text-xs sm:text-sm whitespace-nowrap`}
            >
              {isGenerating ? "Generating..." : "Regenerate"}
            </button>
            <button
              onClick={copyTranscript}
              disabled={isGenerating || !audioData}
              className={`p-2 ${isGenerating ? "text-gray-400" : "text-gray-600 hover:text-purple-700 hover:bg-purple-50"} rounded-md transition-colors flex items-center text-xs sm:text-sm whitespace-nowrap`}
              title="Copy transcript"
            >
              {copied ? <FaCheck className="text-green-500 mr-1" /> : <FaClipboard className="mr-1" />}
              <span>{copied ? "Copied!" : "Copy Transcript"}</span>
            </button>
          </div>
        </div>

        <div className="prose max-w-none mt-4">
          {isGenerating ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-pulse flex flex-col items-center w-full">
                <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          ) : error && !audioData ? (
            <div className="text-red-500 text-center p-4">
              {error}
              <button
                onClick={() => generateAudioExplainer(true)}
                className="block mx-auto mt-4 px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {audioData?.audio_url && (
                <div className="mb-4">
                  <audio controls src={audioData.audio_url} className="w-full">
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
              <p className="text-xs text-gray-500 text-center mt-4">
                AI-generated audio/transcript may contain inaccuracies. Verify important information.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioExplainer;

